import { Title } from '../../../components/typography/Heading';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectFromId, getSpleisProductsForProject } from '../../projects-reducer';
import MaxWidthWrapper from '../../../components/max-width-wrapper/MaxWidthWrapper';
import Main from '../../../frame/main/Main';
import { fetchSubprojectInvitesNotYetSent, sendInvitationToParticipants } from './invite-subproject-actions';
import { useEffect, useState } from 'react';
import LoaderBar from '../../../components/loader-bar/LoaderBar';
import { fetchProjectCard, fetchRewards } from '../../project-actions';
import ProjectContext from '../../project-context';
import RoundedButtonSecondary from '../../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import { AddNewSubprojectInvite } from './AddNewSubprojectInvite';
import { EditSubprojectInvite } from './EditSubprojectInvite';
import filter from 'lodash/filter';
import ButtonText from '../../../components/button/button-text/ButtonText';
import Chevron from '../../../components/chevron/Chevron';
import Image from '../../../components/image/Image';
import ErrorMessage from '../../../components/info-messages/ErrorMessage';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import ShowMore from '../../../components/show-more/ShowMore';
import styles from './invite-subproject.module.less';
import { isProjectADugnad } from '../../project-model';
import { ContextErrorMessage } from '@sb1/ffe-context-message-react';
import { ErrorFieldMessage } from '@sb1/ffe-form-react';
import RoundedImage from '../../../components/RoundedImage';
import { useTitle } from 'hoofd';
import { PrimaryButton } from '@sb1/ffe-buttons-react';
import { fetchSubprojectGenerationLinkForProject } from '../../subproject/subproject-actions';
import { createUrlFromCreationUUID } from '../../subproject-model';
import { trackEvent } from '../../../support-and-tracking/analytics-actions';
import { CopyUrlButton } from '../../../components/copy-url-button/CopyUrlButton';

const InviteSubprojectParticipants = () => {
    const params = useParams();
    const { projectId } = params;
    const [participants, setParticipants] = useState([]);
    const [error, setError] = useState('');
    const [editParticipantId, setEditParticipantId] = useState();
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showInvitation, setShowInvitation] = useState(false);
    const data = useLoaderData();
    const [createSubprojectURL, setCreateSubprojectURL] = useState('');

    const project = useSelector((state) => getProjectFromId(state, projectId));
    const spleisProducts = useSelector((state) => getSpleisProductsForProject(state, projectId));
    const hasSpleisProducts = spleisProducts?.length > 0;
    const isDugnad = isProjectADugnad(project);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchSubprojectGenerationLink = async ({ projectId }) => {
        return dispatch(fetchSubprojectGenerationLinkForProject({ projectId }));
    };

    useEffect(() => {
        fetchSubprojectGenerationLink({ projectId: project.id }).then((result) =>
            setCreateSubprojectURL(createUrlFromCreationUUID({ uuid: result.creation_uuid, projectId: project.id }))
        );
    }, [project?.id]);

    useEffect(() => {
        if (data) {
            setParticipants([...(data.filtered || []), ...(data.users || [])]);
        }
    }, [data]);

    const clearCurrentParticipant = () => {
        setEditParticipantId(undefined);
    };

    useTitle('Deltakere');

    if (!participants) {
        return (
            <Main>
                <MaxWidthWrapper className={styles.container}>
                    <LoaderBar />
                </MaxWidthWrapper>
            </Main>
        );
    }
    const onDeleteParticipant = ({ participantId }) => {
        setParticipants(filter(participants, (p) => p.id !== participantId));
        clearCurrentParticipant();
    };
    const onUpdateParticipant = ({ participant }) => {
        setParticipants(
            participants.map((p) => {
                if (p.id === participant.id) {
                    return participant;
                } else {
                    return p;
                }
            })
        );
        clearCurrentParticipant();
    };
    const onAddParticipant = ({ participant }) => {
        setParticipants([...participants, participant]);
        setShowCreateForm(false);
    };

    const sendInvitation = async () => {
        try {
            await dispatch(sendInvitationToParticipants({ projectId }));
            setError('');
            navigate(`/project/${projectId}/control/subprojects/sent`);
        } catch (e) {
            setError('Det oppsto en feil når vi skulle sende invitasjonen. Prøv igjen senere.');
        }
    };

    const mapParticipant = (participant) => {
        return (
            <div
                className={[
                    styles.participant,
                    editParticipantId === participant.id ? styles.editParticipant : '',
                ].join(' ')}
                onClick={() => {
                    if (editParticipantId !== participant.id) {
                        setEditParticipantId(participant.id);
                        setShowCreateForm(false);
                    }
                }}
                role={'button'}
                aria-description={'Rediger'}
                key={participant.id}
            >
                {editParticipantId === participant.id ? (
                    <EditSubprojectInvite
                        participant={participant}
                        projectId={projectId}
                        onDelete={onDeleteParticipant}
                        onUpdate={onUpdateParticipant}
                    />
                ) : (
                    <>
                        <div className={styles.participantName}>{participant.name}</div>
                        <div className={styles.participantEmail}>
                            {participant.email || <ErrorFieldMessage>Mangler e-postadresse</ErrorFieldMessage>}
                        </div>
                        <div className={styles.participantEdit}>
                            <RoundedButtonSecondary className={styles.participantEditButton}>
                                <i className="far fa-pen-to-square"></i>
                            </RoundedButtonSecondary>
                            <span className={styles.text}>Rediger</span>
                        </div>
                    </>
                )}
            </div>
        );
    };
    // TODO: Replace RoundedImage with Avatar component
    return (
        <Main showAdmin>
            <ProjectContext.Provider value={project}>
                <MaxWidthWrapper className={styles.wrapper}>
                    <div className={styles.container}>
                        <Title>Vi har hentet ut disse deltakerene:</Title>
                        {participants.find((p) => !p.email) && (
                            <ContextErrorMessage compact>
                                Noen deltakere mangler e-post. Legg til e-post, eller send dem invitasjonslenken.
                            </ContextErrorMessage>
                        )}
                        <AddNewSubprojectInvite show={showCreateForm} projectId={projectId} onAdd={onAddParticipant} />
                        {!showCreateForm && (
                            <div className={styles.addParticipant}>
                                <RoundedImage
                                    size={''}
                                    className={styles.addSubprojectBubble}
                                    src={'plus-rounded.svg'}
                                />
                                <ButtonText
                                    onClick={() => {
                                        setShowCreateForm(true);
                                        clearCurrentParticipant();
                                    }}
                                >
                                    Legg til ekstra deltaker
                                </ButtonText>
                            </div>
                        )}
                        <div className={styles.participantList}>
                            <ShowMore
                                items={participants}
                                showCount={5}
                                alwaysShowButtonForDisplayingAll
                                listCreator={(items) => items.map(mapParticipant)}
                            />
                        </div>

                        <div className={styles.bottomContainer}>
                            <div className={styles.information}>
                                <Title>Usikker på e-postadressen?</Title>
                                <p>
                                    Det er også mulig for deltakere å bli med på dugnaden ved at du sender dem
                                    invitasjonslenken.{' '}
                                </p>
                                <CopyUrlButton
                                    prettyProjectUrl={createSubprojectURL}
                                    displayText={'Kopiér invitasjonslenke'}
                                    trackEventFunction={() =>
                                        trackEvent('admin', 'subproject-invite-copy', {
                                            channel: 'invite-copy-link',
                                            activity: 'subproject',
                                            'project-id': project.id,
                                        })
                                    }
                                />
                            </div>
                            <ButtonText onClick={() => setShowInvitation(!showInvitation)}>
                                Se invitasjonen <Chevron isOpen={showInvitation} />
                            </ButtonText>
                            {showInvitation && (
                                <Image
                                    className={styles.invitationImage}
                                    src={
                                        hasSpleisProducts
                                            ? 'dugnad-flyt/dugnad-invite.png'
                                            : 'dugnad-flyt/dugnad-invite-no-products.png'
                                    }
                                />
                            )}
                            {error && <ErrorMessage>{error}</ErrorMessage>}
                            <PrimaryButton
                                className={styles.sendInvitation}
                                onClick={sendInvitation}
                                disabled={participants.length === 0 || editParticipantId || showCreateForm}
                            >
                                Send invitasjon til {isDugnad ? 'dugnad' : 'deltakere'}
                            </PrimaryButton>
                        </div>
                    </div>
                </MaxWidthWrapper>
            </ProjectContext.Provider>
        </Main>
    );
};

export const loader = async ({ params, dispatch }) => {
    const projectId = params.projectId;
    const responses = await Promise.all([
        dispatch(fetchSubprojectInvitesNotYetSent({ projectId })),
        dispatch(fetchProjectCard(projectId, true)),
        dispatch(fetchRewards(projectId, true)),
    ]);
    return responses?.[0];
};

export default InviteSubprojectParticipants;
